import { useState } from "react";
import {
  CLIENT_LINK_RELATION_LABELS_RU,
  type ClientCard,
  type ClientLinkRelation,
} from "@mnp/shared";
import { Icon } from "../../components/Icon";
import { useClients, useAddClientLink } from "../../lib/useClients";

const RELATIONS = Object.entries(CLIENT_LINK_RELATION_LABELS_RU) as [
  ClientLinkRelation,
  string,
][];

/**
 * Привязать клиента: поиск по имени/телефону/@telegram + выбор типа связи.
 * Persists through useAddClientLink (the API mirrors the link bidirectionally).
 */
export function LinkClientModal({
  currentClientId,
  existingLinkIds,
  onClose,
}: {
  currentClientId: string;
  existingLinkIds: string[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<ClientCard | null>(null);
  const [relation, setRelation] = useState<ClientLinkRelation>("spouse");
  const { data } = useClients({ search: query, pageSize: 20 });
  const addLink = useAddClientLink(currentClientId);

  const candidates = (data?.rows ?? []).filter(
    (c) => c.id !== currentClientId && !existingLinkIds.includes(c.id),
  );

  const confirm = () => {
    if (!picked) return;
    addLink.mutate(
      { toChatId: picked.id, relation },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="dlv-modal-wrap" onClick={onClose}>
      <div
        className="dlv-modal lcm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dlv-modal-head">
          <h3>Привязать клиента</h3>
          <button className="btn-admin sm" onClick={onClose}>
            <Icon name="close" size={14} />
          </button>
        </div>

        <div className="dlv-modal-body">
          {!picked && (
            <>
              <div className="lcm-search">
                <Icon name="search" size={14} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ФИО, телефон или @telegram"
                  aria-label="Поиск клиента"
                  autoFocus
                />
              </div>
              <div className="lcm-results">
                {candidates.length === 0 && (
                  <div className="lcm-empty">
                    Никого не нашли. Попробуйте по-другому.
                  </div>
                )}
                {candidates.map((cl) => (
                  <button
                    key={cl.id}
                    className="lcm-result"
                    onClick={() => setPicked(cl)}
                  >
                    <div className={"cd-link-avatar status-" + cl.status}>
                      {cl.initials}
                    </div>
                    <div className="lcm-result-meta">
                      <p className="lcm-result-name">{cl.name}</p>
                      <p className="lcm-result-sub mono">
                        {cl.tgUsername ?? cl.phone ?? cl.id}
                      </p>
                    </div>
                    <Icon name="chevRight" size={14} />
                  </button>
                ))}
              </div>
            </>
          )}

          {picked && (
            <>
              <div className="lcm-picked">
                <div className={"cd-link-avatar status-" + picked.status}>
                  {picked.initials}
                </div>
                <div className="lcm-picked-meta">
                  <p className="lcm-picked-name">{picked.name}</p>
                  <p className="lcm-picked-sub mono">
                    {picked.tgUsername ?? picked.phone ?? picked.id}
                  </p>
                </div>
                <button
                  className="lcm-unpick"
                  onClick={() => setPicked(null)}
                >
                  Сменить
                </button>
              </div>

              <p className="dlv-mini-list-title">Тип связи</p>
              <div className="lcm-relations">
                {RELATIONS.map(([value, label]) => (
                  <label
                    key={value}
                    className={
                      "lcm-rel " + (relation === value ? "is-active" : "")
                    }
                  >
                    <input
                      type="radio"
                      name="relation"
                      checked={relation === value}
                      onChange={() => setRelation(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>

              <div className="lcm-note">
                <span>ℹ️</span>
                <span>
                  Связанные клиенты могут забирать заказы друг друга и платить
                  за чужие посылки.
                </span>
              </div>
            </>
          )}
        </div>

        <div className="dlv-modal-foot">
          <button className="btn-admin" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn-admin primary lg"
            disabled={!picked || addLink.isPending}
            onClick={confirm}
          >
            Привязать
          </button>
        </div>
      </div>
    </div>
  );
}
